import { useEffect, useMemo, useState } from "react";
import { AdminButton } from "@/components/admin/button";
import { CameroonMap } from "@/components/admin/cameroon-map";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import {
	useCreateCity,
	useCreateNeighborhood,
	useDeleteCity,
	useDeleteNeighborhood,
	useDeliveryCities,
	useDeliveryNeighborhoods,
	useDeliveryRegions,
	useUpdateCity,
	useUpdateNeighborhood,
	useUpdateRegion,
} from "@/hooks/use-delivery";
import { useHubs } from "@/hooks/use-logistics";
import { fmtFCFA } from "@/lib/format";
import type {
	DeliveryCity,
	DeliveryNeighborhood,
	DeliveryRegion,
	UpsertCityInput,
	UpsertNeighborhoodInput,
} from "@/lib/logistics/delivery-api";

const LEGEND: { label: string; fill: string; stroke: string }[] = [
	{
		label: "Desservie",
		fill: "var(--a-forest-soft)",
		stroke: "var(--a-forest)",
	},
	{
		label: "Sans ville",
		fill: "var(--a-ochre-soft)",
		stroke: "var(--a-ochre)",
	},
	{
		label: "Inactive",
		fill: "var(--a-surface-2)",
		stroke: "var(--a-border-strong)",
	},
	{
		label: "Sélection",
		fill: "var(--a-terracotta)",
		stroke: "var(--a-terra-deep)",
	},
];

export function AdminDeliveryZonesPage() {
	const { data: regions = [], isLoading } = useDeliveryRegions();
	const [selectedCode, setSelectedCode] = useState<string | null>(null);

	// Pré-sélection : première région active dès le chargement.
	useEffect(() => {
		if (!selectedCode && regions.length > 0) {
			const first = regions.find((r) => r.isActive) ?? regions[0];
			setSelectedCode(first.code);
		}
	}, [regions, selectedCode]);

	const selected = useMemo(
		() => regions.find((r) => r.code === selectedCode) ?? null,
		[regions, selectedCode],
	);

	const totalCities = regions.reduce((s, r) => s + r.activeCityCount, 0);
	const activeRegions = regions.filter(
		(r) => r.isActive && r.activeCityCount > 0,
	).length;

	return (
		<>
			<PageHead
				title="Zones de livraison"
				sub="Carte interactive du Cameroun — tarifs de livraison par région et par ville"
			/>

			<div
				className="kpi-grid"
				style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
			>
				<div className="kpi accent">
					<div className="kpi-l">Régions desservies</div>
					<div className="kpi-v tn">
						{activeRegions}
						<small> / 10</small>
					</div>
					<div className="kpi-foot muted">au moins une ville active</div>
				</div>
				<div className="kpi forest">
					<div className="kpi-l">Villes livrables</div>
					<div className="kpi-v tn">{totalCities}</div>
					<div className="kpi-foot muted">tarifées et actives</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Région sélectionnée</div>
					<div className="kpi-v" style={{ fontSize: 20 }}>
						{selected?.name ?? "—"}
					</div>
					<div className="kpi-foot muted">
						{selected
							? `chef-lieu : ${selected.chefLieu ?? "—"}`
							: "cliquez la carte"}
					</div>
				</div>
			</div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: "minmax(340px, 420px) 1fr",
					gap: 16,
					alignItems: "start",
				}}
				className="dz-layout"
			>
				{/* Colonne gauche : carte + légende + éditeur région */}
				<div className="stack">
					<Card>
						<CardHead
							title="Carte des régions"
							sub="Cliquez une région pour gérer ses villes"
						/>
						<CardBody>
							{isLoading ? (
								<div className="empty">Chargement de la carte…</div>
							) : (
								<CameroonMap
									regions={regions}
									selected={selectedCode}
									onSelect={setSelectedCode}
								/>
							)}
							<div
								style={{
									display: "flex",
									flexWrap: "wrap",
									gap: 12,
									marginTop: 12,
								}}
							>
								{LEGEND.map((l) => (
									<span
										key={l.label}
										style={{
											display: "inline-flex",
											alignItems: "center",
											gap: 6,
											fontSize: 11.5,
											color: "var(--a-ink-soft)",
										}}
									>
										<span
											style={{
												width: 14,
												height: 14,
												borderRadius: 3,
												background: l.fill,
												border: `1.5px solid ${l.stroke}`,
											}}
										/>
										{l.label}
									</span>
								))}
							</div>
						</CardBody>
					</Card>

					{selected && <RegionEditor region={selected} />}
				</div>

				{/* Colonne droite : villes de la région sélectionnée */}
				<div className="stack">
					{selected ? (
						<CitiesPanel region={selected} />
					) : (
						<Card>
							<CardBody>
								<div className="empty">
									Sélectionnez une région sur la carte pour gérer ses villes.
								</div>
							</CardBody>
						</Card>
					)}
				</div>
			</div>

			<style>{`
        @media (max-width: 900px) {
          .dz-layout { grid-template-columns: 1fr !important; }
        }
      `}</style>
		</>
	);
}

// ─────────────────────────────────────────────────────────────
// Éditeur de région (tarif par défaut + activation)
// ─────────────────────────────────────────────────────────────

function RegionEditor({ region }: { region: DeliveryRegion }) {
	const update = useUpdateRegion();
	const toast = useToast();
	const [defaultFee, setDefaultFee] = useState(String(region.defaultFee));
	const [isActive, setIsActive] = useState(region.isActive);

	useEffect(() => {
		setDefaultFee(String(region.defaultFee));
		setIsActive(region.isActive);
	}, [region.id, region.defaultFee, region.isActive]);

	const dirty =
		Number(defaultFee) !== region.defaultFee || isActive !== region.isActive;

	const save = async () => {
		try {
			await update.mutateAsync({
				id: region.id,
				body: { defaultFee: Math.max(0, Number(defaultFee) || 0), isActive },
			});
			toast.success("Région mise à jour");
		} catch {
			toast.error("Échec de la mise à jour");
		}
	};

	return (
		<Card>
			<CardHead
				title={region.name}
				sub={`Région ${region.code}`}
				right={
					<Tag variant={isActive ? "success" : "neutral"}>
						{isActive ? "Active" : "Inactive"}
					</Tag>
				}
			/>
			<CardBody>
				<label className="field-label" style={{ marginBottom: 4 }}>
					Tarif par défaut (villes non listées)
				</label>
				<div className="aform-with-suffix" style={{ marginBottom: 14 }}>
					<input
						className="aform-i"
						type="number"
						min={0}
						step={500}
						value={defaultFee}
						onChange={(e) => setDefaultFee(e.target.value)}
					/>
					<span>FCFA</span>
				</div>

				<label className="checkbox" style={{ marginBottom: 16 }}>
					<input
						type="checkbox"
						checked={isActive}
						onChange={(e) => setIsActive(e.target.checked)}
					/>
					Région desservie (affichée au checkout)
				</label>

				<AdminButton
					variant="primary"
					onClick={save}
					disabled={!dirty || update.isPending}
					style={{ width: "100%" }}
				>
					{update.isPending ? "Enregistrement…" : "Enregistrer la région"}
				</AdminButton>
			</CardBody>
		</Card>
	);
}

// ─────────────────────────────────────────────────────────────
// Panneau des villes d'une région
// ─────────────────────────────────────────────────────────────

function CitiesPanel({ region }: { region: DeliveryRegion }) {
	const { data: cities = [], isLoading } = useDeliveryCities(region.code);
	const del = useDeleteCity();
	const confirm = useConfirm();
	const toast = useToast();
	const [editing, setEditing] = useState<DeliveryCity | null>(null);
	const [creating, setCreating] = useState(false);
	const [quartiersDe, setQuartiersDe] = useState<DeliveryCity | null>(null);

	const onDelete = async (c: DeliveryCity) => {
		const ok = await confirm({
			title: `Supprimer ${c.name} ?`,
			message:
				"Cette ville ne sera plus proposée au checkout. Les commandes passées ne sont pas affectées.",
			tone: "danger",
			confirmLabel: "Supprimer",
			requireText: c.name,
			requireHint: (
				<>
					Tapez <strong>{c.name}</strong> pour confirmer
				</>
			),
		});
		if (!ok) return;
		try {
			await del.mutateAsync(c.id);
			toast.success("Ville supprimée");
		} catch {
			toast.error("Échec de la suppression");
		}
	};

	return (
		<>
			<Card>
				<CardHead
					title={`Villes — ${region.name}`}
					sub={`${cities.length} ville${cities.length > 1 ? "s" : ""} tarifée${cities.length > 1 ? "s" : ""}`}
					right={
						<AdminButton
							variant="primary"
							size="sm"
							onClick={() => setCreating(true)}
						>
							<AdminIcon n="plus" s={14} /> Ajouter une ville
						</AdminButton>
					}
				/>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Ville</th>
								<th className="right">Standard</th>
								<th className="right">Express</th>
								<th>Délai</th>
								<th>Statut</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td
										colSpan={6}
										className="muted center"
										style={{ padding: 24 }}
									>
										Chargement…
									</td>
								</tr>
							)}
							{!isLoading && cities.length === 0 && (
								<tr>
									<td
										colSpan={6}
										className="muted center"
										style={{ padding: 24 }}
									>
										Aucune ville. Ajoutez-en une pour desservir cette région.
									</td>
								</tr>
							)}
							{cities.map((c) => (
								<tr key={c.id}>
									<td>
										<strong>{c.name}</strong>
									</td>
									<td className="right tn">{fmtFCFA(c.fee)}</td>
									<td className="right tn muted">
										{c.expressFee != null ? fmtFCFA(c.expressFee) : "—"}
									</td>
									<td className="muted">
										{c.etaMinHours}–{c.etaMaxHours} h
									</td>
									<td>
										<Tag variant={c.isActive ? "success" : "neutral"}>
											{c.isActive ? "Active" : "Inactive"}
										</Tag>
									</td>
									<td className="right" style={{ whiteSpace: "nowrap" }}>
										<AdminButton
											variant="ghost"
											size="sm"
											onClick={() => setQuartiersDe(c)}
											style={{ marginRight: 4 }}
										>
											<AdminIcon n="pin" s={14} /> Quartiers
										</AdminButton>
										<AdminButton
											variant="ghost"
											size="sm"
											iconOnly
											onClick={() => setEditing(c)}
											aria-label="Modifier"
										>
											<AdminIcon n="edit" s={14} />
										</AdminButton>
										<AdminButton
											variant="ghost"
											size="sm"
											iconOnly
											onClick={() => onDelete(c)}
											aria-label="Supprimer"
											style={{ marginLeft: 4 }}
										>
											<AdminIcon n="close" s={14} />
										</AdminButton>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Card>

			{quartiersDe && (
				<NeighborhoodsModal
					city={quartiersDe}
					onClose={() => setQuartiersDe(null)}
				/>
			)}

			{(creating || editing) && (
				<CityFormModal
					region={region}
					city={editing}
					onClose={() => {
						setCreating(false);
						setEditing(null);
					}}
				/>
			)}
		</>
	);
}

// ─────────────────────────────────────────────────────────────
// Formulaire ville (création / édition)
// ─────────────────────────────────────────────────────────────

function CityFormModal({
	region,
	city,
	onClose,
}: {
	region: DeliveryRegion;
	city: DeliveryCity | null;
	onClose: () => void;
}) {
	const create = useCreateCity();
	const update = useUpdateCity();
	const toast = useToast();
	const { data: hubs = [] } = useHubs();

	const [name, setName] = useState(city?.name ?? "");
	const [fee, setFee] = useState(String(city?.fee ?? region.defaultFee));
	const [expressFee, setExpressFee] = useState(
		city?.expressFee != null ? String(city.expressFee) : "",
	);
	const [freeThreshold, setFreeThreshold] = useState(
		city?.freeThreshold != null ? String(city.freeThreshold) : "",
	);
	const [etaMin, setEtaMin] = useState(String(city?.etaMinHours ?? 24));
	const [etaMax, setEtaMax] = useState(String(city?.etaMaxHours ?? 72));
	const [hubId, setHubId] = useState(city?.hubId ?? "");
	const [isActive, setIsActive] = useState(city?.isActive ?? true);

	const pending = create.isPending || update.isPending;
	const valid =
		name.trim().length > 0 &&
		Number(fee) >= 0 &&
		Number(etaMin) <= Number(etaMax);

	const submit = async () => {
		if (!valid) return;
		const body: UpsertCityInput = {
			regionCode: region.code,
			name: name.trim(),
			fee: Math.max(0, Number(fee) || 0),
			expressFee:
				expressFee.trim() === "" ? null : Math.max(0, Number(expressFee) || 0),
			freeThreshold:
				freeThreshold.trim() === ""
					? null
					: Math.max(0, Number(freeThreshold) || 0),
			etaMinHours: Math.max(0, Number(etaMin) || 0),
			etaMaxHours: Math.max(0, Number(etaMax) || 0),
			hubId: hubId || null,
			isActive,
		};
		try {
			if (city) await update.mutateAsync({ id: city.id, body });
			else await create.mutateAsync(body);
			toast.success(city ? "Ville mise à jour" : "Ville ajoutée");
			onClose();
		} catch (e) {
			toast.error((e as Error).message || "Échec de l’enregistrement");
		}
	};

	return (
		<Modal
			title={city ? `Modifier ${city.name}` : `Nouvelle ville — ${region.name}`}
			subtitle={`Région ${region.name} (${region.code})`}
			onClose={onClose}
			width={520}
		>
			<div style={{ display: "grid", gap: 12 }}>
				<div>
					<label className="field-label">Nom de la ville *</label>
					<input
						className="aform-i"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Ex. Douala"
					/>
				</div>

				<div
					style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
				>
					<div>
						<label className="field-label">Frais standard (FCFA) *</label>
						<input
							className="aform-i"
							type="number"
							min={0}
							step={500}
							value={fee}
							onChange={(e) => setFee(e.target.value)}
						/>
					</div>
					<div>
						<label className="field-label">Frais express (FCFA)</label>
						<input
							className="aform-i"
							type="number"
							min={0}
							step={500}
							value={expressFee}
							onChange={(e) => setExpressFee(e.target.value)}
							placeholder="auto (+2 000)"
						/>
					</div>
				</div>

				<div
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr 1fr",
						gap: 12,
					}}
				>
					<div>
						<label className="field-label">Délai min (h)</label>
						<input
							className="aform-i"
							type="number"
							min={0}
							value={etaMin}
							onChange={(e) => setEtaMin(e.target.value)}
						/>
					</div>
					<div>
						<label className="field-label">Délai max (h)</label>
						<input
							className="aform-i"
							type="number"
							min={0}
							value={etaMax}
							onChange={(e) => setEtaMax(e.target.value)}
						/>
					</div>
					<div>
						<label className="field-label">Gratuité dès (FCFA)</label>
						<input
							className="aform-i"
							type="number"
							min={0}
							step={1000}
							value={freeThreshold}
							onChange={(e) => setFreeThreshold(e.target.value)}
							placeholder="national"
						/>
					</div>
				</div>

				<div>
					<label className="field-label">Hub logistique rattaché</label>
					<select
						className="aform-i"
						value={hubId}
						onChange={(e) => setHubId(e.target.value)}
					>
						<option value="">Automatique / non défini</option>
						{hubs.map((h) => (
							<option key={h.id} value={h.id}>
								{h.name} ({h.code})
							</option>
						))}
					</select>
				</div>

				<label className="checkbox">
					<input
						type="checkbox"
						checked={isActive}
						onChange={(e) => setIsActive(e.target.checked)}
					/>
					Ville active (proposée au checkout)
				</label>

				{!valid && (
					<p className="field-help error" style={{ margin: 0 }}>
						Renseignez un nom et vérifiez que le délai min ≤ délai max.
					</p>
				)}
			</div>

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				<AdminButton
					variant="primary"
					onClick={submit}
					disabled={!valid || pending}
				>
					{pending
						? "Enregistrement…"
						: city
							? "Enregistrer"
							: "Ajouter la ville"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}

// ─────────────────────────────────────────────────────────────
// Quartiers d'une ville — c'est eux qui fixent le tarif facturé
// ─────────────────────────────────────────────────────────────

/**
 * Le tarif s'arrêtait à la ville : livrer à Bastos ou à Mfou coûtait pareil au
 * client alors que la tournée n'a rien à voir. Les quartiers portent désormais
 * le prix, et se gèrent ici sans déploiement — la liste initiale (Yaoundé,
 * Douala) est faite pour être affinée au fil des tournées réelles.
 */
function NeighborhoodsModal({
	city,
	onClose,
}: {
	city: DeliveryCity;
	onClose: () => void;
}) {
	const { data: quartiers = [], isLoading } = useDeliveryNeighborhoods(city.id);
	const del = useDeleteNeighborhood();
	const confirm = useConfirm();
	const toast = useToast();
	const [edite, setEdite] = useState<DeliveryNeighborhood | null>(null);
	const [cree, setCree] = useState(false);

	const supprimer = async (q: DeliveryNeighborhood) => {
		const ok = await confirm({
			title: `Supprimer ${q.name} ?`,
			message: `Les commandes vers ${q.name} retomberont sur le tarif de ${city.name} (${fmtFCFA(city.fee)}). Les commandes déjà passées ne changent pas.`,
			tone: "danger",
			confirmLabel: "Supprimer",
		});
		if (!ok) return;
		try {
			await del.mutateAsync(q.id);
			toast.success("Quartier supprimé");
		} catch {
			toast.error("Échec de la suppression");
		}
	};

	return (
		<>
			<Modal
				title={`Quartiers — ${city.name}`}
				subtitle={`Tarif de repli de la ville : ${fmtFCFA(city.fee)}`}
				onClose={onClose}
				width={720}
			>
				<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
					<AdminButton variant="primary" size="sm" onClick={() => setCree(true)}>
						<AdminIcon n="plus" s={14} /> Ajouter un quartier
					</AdminButton>
				</div>

				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Quartier</th>
								<th className="right">Standard</th>
								<th className="right">Express</th>
								<th>Délai</th>
								<th>Statut</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td colSpan={6} className="muted center" style={{ padding: 24 }}>
										Chargement…
									</td>
								</tr>
							)}
							{!isLoading && quartiers.length === 0 && (
								<tr>
									<td colSpan={6} className="muted center" style={{ padding: 24 }}>
										Aucun quartier : le checkout facture le tarif de la ville et
										laisse le client saisir son quartier librement.
									</td>
								</tr>
							)}
							{quartiers.map((q) => (
								<tr key={q.id}>
									<td>
										<strong>{q.name}</strong>
									</td>
									<td className="right tn">{fmtFCFA(q.fee)}</td>
									<td className="right tn muted">
										{q.expressFee != null ? fmtFCFA(q.expressFee) : "—"}
									</td>
									<td className="muted">
										{q.etaMinHours}–{q.etaMaxHours} h
									</td>
									<td>
										<Tag variant={q.isActive ? "success" : "neutral"}>
											{q.isActive ? "Actif" : "Suspendu"}
										</Tag>
									</td>
									<td className="right" style={{ whiteSpace: "nowrap" }}>
										<AdminButton
											variant="ghost"
											size="sm"
											iconOnly
											onClick={() => setEdite(q)}
											aria-label="Modifier"
										>
											<AdminIcon n="edit" s={14} />
										</AdminButton>
										<AdminButton
											variant="ghost"
											size="sm"
											iconOnly
											onClick={() => supprimer(q)}
											aria-label="Supprimer"
											style={{ marginLeft: 4 }}
										>
											<AdminIcon n="close" s={14} />
										</AdminButton>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<ModalFooter>
					<AdminButton variant="ghost" onClick={onClose}>
						Fermer
					</AdminButton>
				</ModalFooter>
			</Modal>

			{(cree || edite) && (
				<NeighborhoodFormModal
					city={city}
					quartier={edite}
					onClose={() => {
						setCree(false);
						setEdite(null);
					}}
				/>
			)}
		</>
	);
}

function NeighborhoodFormModal({
	city,
	quartier,
	onClose,
}: {
	city: DeliveryCity;
	quartier: DeliveryNeighborhood | null;
	onClose: () => void;
}) {
	const create = useCreateNeighborhood();
	const update = useUpdateNeighborhood();
	const toast = useToast();

	const [name, setName] = useState(quartier?.name ?? "");
	const [fee, setFee] = useState(String(quartier?.fee ?? city.fee));
	const [expressFee, setExpressFee] = useState(
		quartier?.expressFee != null ? String(quartier.expressFee) : "",
	);
	const [etaMin, setEtaMin] = useState(String(quartier?.etaMinHours ?? city.etaMinHours));
	const [etaMax, setEtaMax] = useState(String(quartier?.etaMaxHours ?? city.etaMaxHours));
	const [isActive, setIsActive] = useState(quartier?.isActive ?? true);

	const pending = create.isPending || update.isPending;
	const valid =
		name.trim().length > 0 &&
		Number(fee) >= 0 &&
		Number(etaMin) <= Number(etaMax);

	const submit = async () => {
		if (!valid) return;
		const body: UpsertNeighborhoodInput = {
			cityId: city.id,
			name: name.trim(),
			fee: Math.max(0, Number(fee) || 0),
			expressFee:
				expressFee.trim() === "" ? null : Math.max(0, Number(expressFee) || 0),
			etaMinHours: Math.max(0, Number(etaMin) || 0),
			etaMaxHours: Math.max(0, Number(etaMax) || 0),
			isActive,
		};
		try {
			if (quartier) await update.mutateAsync({ id: quartier.id, body });
			else await create.mutateAsync(body);
			toast.success(quartier ? "Quartier mis à jour" : "Quartier ajouté");
			onClose();
		} catch (e) {
			toast.error((e as Error).message || "Échec de l’enregistrement");
		}
	};

	return (
		<Modal
			title={quartier ? `Modifier ${quartier.name}` : `Nouveau quartier — ${city.name}`}
			subtitle={`Ville ${city.name} · repli ${fmtFCFA(city.fee)}`}
			onClose={onClose}
			width={520}
		>
			<div style={{ display: "grid", gap: 12 }}>
				<div>
					<label className="field-label">Nom du quartier *</label>
					<input
						className="aform-i"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Ex. Bastos"
					/>
					<p className="field-help" style={{ margin: "4px 0 0" }}>
						Le nom doit être celui que le client reconnaît : c’est lui qu’il
						choisit dans la liste au moment de commander.
					</p>
				</div>

				<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
					<div>
						<label className="field-label">Frais standard (FCFA) *</label>
						<input
							className="aform-i"
							type="number"
							min={0}
							step={250}
							value={fee}
							onChange={(e) => setFee(e.target.value)}
						/>
					</div>
					<div>
						<label className="field-label">Frais express (FCFA)</label>
						<input
							className="aform-i"
							type="number"
							min={0}
							step={500}
							value={expressFee}
							onChange={(e) => setExpressFee(e.target.value)}
							placeholder="tarif de la ville"
						/>
					</div>
				</div>

				<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
					<div>
						<label className="field-label">Délai min (h)</label>
						<input
							className="aform-i"
							type="number"
							min={0}
							value={etaMin}
							onChange={(e) => setEtaMin(e.target.value)}
						/>
					</div>
					<div>
						<label className="field-label">Délai max (h)</label>
						<input
							className="aform-i"
							type="number"
							min={0}
							value={etaMax}
							onChange={(e) => setEtaMax(e.target.value)}
						/>
					</div>
				</div>

				<label className="checkbox">
					<input
						type="checkbox"
						checked={isActive}
						onChange={(e) => setIsActive(e.target.checked)}
					/>
					Quartier desservi (proposé au checkout)
				</label>

				{!valid && (
					<p className="field-help error" style={{ margin: 0 }}>
						Renseignez un nom et vérifiez que le délai min ≤ délai max.
					</p>
				)}
			</div>

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				<AdminButton variant="primary" onClick={submit} disabled={!valid || pending}>
					{pending ? "Enregistrement…" : quartier ? "Enregistrer" : "Ajouter"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
